import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import router from '@/router'
import { debounce } from "lodash";
import useFilterCounter from '@/components/Filters/useFiltersCount'
import axios from "@axios";

export default function useList() {
  const refListTable = ref(null);
  const toast = useToast();

  const tableColumns = [
    { label: "seproban.images", key: "images", sortable: true },
    { label: "unit.description", key: "unit_name", sortable: true },
    { label: "camera.title", key: "camera_name", sortable: true },
    { label: "seproban.total_images", key: "total_images", sortable: true },
    { label: "actions.download", key: "download", sortable: false },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
    {
      label: "date.updated_at",
      key: "updated_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  const unit = ref({})
  const alarm = ref({})
  const imageRows = ref([])
  const eventDetails = ref({})

  const fetchEventDetails = async alarmId => {
    if (!alarmId || Number(eventDetails.value.id_alarm) === Number(alarmId)) return;

    try {
      const response = await axios.get("/v1/seproban/events", {
        params: {
          id_alarm: alarmId,
          with_snapshots: true,
          max: 1,
          sortBy: "id",
          sortDesc: true,
        },
      });
      const event = response.data.data?.[0] || {};
      if (Number(event.id) !== Number(idSeprobanAlarmEvent)) return;

      eventDetails.value = event;
      if (event.unit) unit.value = event.unit;
    } catch (error) {
      console.error("Error obteniendo el detalle del evento Seproban:", error);
    }
  };

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  let idSeprobanAlarmEvent = 0
  if (router.currentRoute.params.id_event) {
    idSeprobanAlarmEvent = parseInt(router.currentRoute.params.id_event)
  }

  const cameraFilter = ref([]);
  const downloadFromDate = ref("");
  const downloadToDate = ref("");
  const sentFromDate = ref("");
  const sentToDate = ref("");
  const downloadDateRange = ref("");
  const sentDateRange = ref("");
  const cameraOptions = computed(() => (eventDetails.value.cameras || []).map(camera => ({
    label: camera.camera_name,
    value: camera.camera_proxy_id,
  })));

  const splitDateRange = value => {
    if (!value) return { from: null, to: null };
    const [from, to] = value.split(/\s+to\s+/).map(date => date.trim());
    const formats = ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss"];
    let fromDate = moment(from, formats, true);
    let toDate = to ? moment(to, formats, true) : null;

    if (!fromDate.isValid() || (toDate && !toDate.isValid())) {
      return { from: null, to: null };
    }

    if (toDate && fromDate.isAfter(toDate)) {
      [fromDate, toDate] = [toDate, fromDate];
    }

    return {
      from: fromDate.format("YYYY-MM-DD HH:mm:ss"),
      to: toDate ? toDate.format("YYYY-MM-DD HH:mm:ss") : null,
    };
  };

  const getFilterParams = () => {
    const downloaded = splitDateRange(downloadDateRange.value);
    const sent = splitDateRange(sentDateRange.value);

    return {
      id_camera_proxy: cameraFilter.value,
      downloaded_at_from: downloadFromDate.value,
      downloaded_at_to: downloadToDate.value,
      sent_at_from: sentFromDate.value,
      sent_at_to: sentToDate.value,
    };
  };

  const fetchList = (ctx, callback) => {
    store
      .dispatch("seproban_images/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        id_seproban_alarm_event: idSeprobanAlarmEvent,
        ...getFilterParams(),
      })
      .then((response) => {
        callback(response.data);
        imageRows.value = response.data || [];
        unit.value = response.data[0]?.unit || {};
        alarm.value = response.data[0]?.alarm || {};
        fetchEventDetails(alarm.value.id);
        totalList.value = response.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      id_camera_proxy: cameraFilter.value,
      download_date_range: downloadDateRange.value,
      sent_date_range: sentDateRange.value,
      downloaded_at_from: downloadFromDate.value,
      downloaded_at_to: downloadToDate.value,
      sent_at_from: sentFromDate.value,
      sent_at_to: sentToDate.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    cameraFilter.value = [];
    downloadDateRange.value = "";
    downloadFromDate.value = "";
    downloadToDate.value = "";
    sentFromDate.value = "";
    sentToDate.value = "";
    sentDateRange.value = "";
    buildFilters();
  }

  const downloadSnapshots = (idCameraProxy = null) => store.dispatch(
    "seproban_images/downloadZip",
    {
      id_seproban_alarm_event: idSeprobanAlarmEvent,
      ...getFilterParams(),
      ...(idCameraProxy != null ? { id_camera_proxy: [idCameraProxy] } : {}),
    }
  );

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    cameraOptions,
    cameraFilter,
    downloadFromDate,
    downloadToDate,
    sentDateRange,
    sentFromDate,
    sentToDate,
    filtersCount,
    unit,
    alarm,
    imageRows,
    eventDetails,
    refetchData,
    clearSearchFilters,
    buildFilters,
    downloadSnapshots,
    idSeprobanAlarmEvent,
  };
}
